import React, { useCallback, useMemo, useRef, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import "../css/CustomerInfo.css";

const parseUsDate = (mmddyyyy) => {
  if (!mmddyyyy || typeof mmddyyyy !== "string") return null;
  const [m, d, y] = mmddyyyy.split("/").map(Number);
  if (!m || !d || !y) return null;
  return new Date(y, m - 1, d); // local date
};

const asCurrency = (n) => `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

export default function CustomerInfo() {
  const [rawPhone, setRawPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState("");

  // For printing only the results area
  const printRef = useRef(null);

  const normalizedPhone = useMemo(
    () => (rawPhone || "").replace(/\D/g, ""),
    [rawPhone]
  );

  const onSearch = useCallback(async () => {
    setTouched(true);
    setError("");
    if (normalizedPhone.length < 10) {
      setTransactions([]);
      setCustomerName("");
      return;
    }

    try {
      setLoading(true);
      const qRef = query(
        collection(db, "transactions"),
        where("PhoneNum", "==", normalizedPhone)
      );
      const snap = await getDocs(qRef);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const sorted = rows.sort((a, b) => {
        const da = parseUsDate(a.Date) || new Date(0);
        const dbb = parseUsDate(b.Date) || new Date(0);
        if (dbb - da !== 0) return dbb - da;
        return String(b.Time || "").localeCompare(String(a.Time || ""));
      });

      setTransactions(sorted);
      setCustomerName(sorted[0]?.PName || "Unknown");
    } catch (e) {
      console.error("Error fetching customer transactions:", e);
      setError("Could not fetch transactions. Please try again.");
      setTransactions([]);
      setCustomerName("");
    } finally {
      setLoading(false);
    }
  }, [normalizedPhone]);

  // Metrics (includes free visit count: TotalAmount === 0)
  const metrics = useMemo(() => {
    if (!transactions.length) return null;

    const visits = transactions.length;
    const total = transactions.reduce((s, t) => s + (t.TotalAmount || 0), 0);
    const cash = transactions.reduce((s, t) => s + (t.TotalCashAmount || 0), 0);
    const card = transactions.reduce((s, t) => s + (t.TotalCardAmount || 0), 0);
    const lastVisitDate = transactions[0]?.Date || "";
    const lastVisitTime = transactions[0]?.Time || "";
    const freeVisits = transactions.filter(
      (t) => Number(t.TotalAmount || 0) === 0
    ).length;
    const note =
      transactions.find((t) => String(t.CustomerNote || "").trim() !== "")
        ?.CustomerNote || "";

    return {
      visits,
      total,
      cash,
      card,
      freeVisits,
      lastVisit: `${lastVisitDate} ${lastVisitTime}`.trim(),
      note,
    };
  }, [transactions]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") onSearch();
  };

  const showEmpty =
    touched &&
    normalizedPhone.length >= 10 &&
    !loading &&
    !transactions.length &&
    !error;

  const handlePrint = () => {
    if (!transactions.length) return;
    // Add a class to limit print to the section
    document.body.classList.add("printing");
    window.print();
    // Remove class after print
    const onAfterPrint = () => {
      document.body.classList.remove("printing");
      window.removeEventListener("afterprint", onAfterPrint);
    };
    window.addEventListener("afterprint", onAfterPrint);
  };

  return (
    <div className="customer-info">
      <h2>Customer Transactions</h2>

      <div className="search-container">
        <div className="search-row">
          <label htmlFor="phone">Phone Number</label>
          <div className="search-inputs">
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              value={rawPhone}
              onChange={(e) => setRawPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 8185551234"
              className="phone-input"
              aria-label="Customer phone number"
            />
            <button
              onClick={onSearch}
              disabled={loading || normalizedPhone.length < 10}
              className="btn-primary"
            >
              {loading ? "Searching…" : "Search"}
            </button>
            {transactions.length > 0 && (
              <button
                type="button"
                onClick={handlePrint}
                className="btn-ghost"
                title="Print this customer's history"
              >
                Print
              </button>
            )}
          </div>
          <div className="help-text">
            Enter at least 10 digits. We ignore dashes/spaces automatically.
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* PRINT AREA */}
      <div className="print-area" ref={printRef}>
        {metrics && (
          <>
            <div className="legend">
              <span className="badge badge-free">Free Visit</span>
              <span className="legend-text">
                (Any row with total = $0 is a free visit; policy: every 6th
                visit is free)
              </span>
            </div>

            <div className="metrics">
              <div className="metric-card">
                <div className="metric-label">Customer</div>
                <div className="metric-value">{customerName}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Visits</div>
                <div className="metric-value">{metrics.visits}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Free Visits</div>
                <div className="metric-value">{metrics.freeVisits}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Total Spent</div>
                <div className="metric-value">{asCurrency(metrics.total)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Cash</div>
                <div className="metric-value">{asCurrency(metrics.cash)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Card</div>
                <div className="metric-value">{asCurrency(metrics.card)}</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Last Visit</div>
                <div className="metric-value">{metrics.lastVisit || "—"}</div>
              </div>
            </div>

            <div className="customer-note-section">
              <div className="customer-note-title">Customer Note</div>
              <div className="customer-note-body">
                {metrics.note ? (
                  <div className="customer-note-text">{metrics.note}</div>
                ) : (
                  <span className="muted">—</span>
                )}
              </div>
            </div>
          </>
        )}

        {showEmpty && (
          <p className="empty-state">
            No transactions found for this customer.
          </p>
        )}

        {transactions.length > 0 && (
          <div className="table-wrapper">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 110 }}>Time</th>
                  <th>Payment</th>
                  <th className="td-right">Play</th>
                  <th>Food Orders</th>
                  <th className="td-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, i) => {
                  const isFree = Number(t.TotalAmount || 0) === 0;
                  return (
                    <tr
                      key={t.id || i}
                      className={isFree ? "row-free" : undefined}
                    >
                      <td>{t.Date || "—"}</td>
                      <td>{t.Time || "—"}</td>
                      <td>
                        <span
                          className={`badge ${
                            (t.PayMethod || "").includes("Card")
                              ? "badge-blue"
                              : (t.PayMethod || "").includes("Cash")
                              ? "badge-green"
                              : "badge-gray"
                          }`}
                        >
                          {t.PayMethod || "—"}
                        </span>
                      </td>
                      <td className="td-right">
                        {asCurrency(t.PlayCost || 0)}
                      </td>
                      <td>
                        {t.FoodOrder?.length ? (
                          t.FoodOrder.map((f, idx) => (
                            <div key={idx} className="food-line">
                              {f.food} <span className="x">×</span> {f.quantity}
                            </div>
                          ))
                        ) : (
                          <span className="muted">None</span>
                        )}
                      </td>
                      <td className="td-right">
                        <strong>{asCurrency(t.TotalAmount || 0)}</strong>
                        {isFree && (
                          <span className="pill pill-free">Free Visit</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Totals</td>
                  <td className="td-right">
                    {asCurrency(
                      transactions.reduce((s, t) => s + (t.PlayCost || 0), 0)
                    )}
                  </td>
                  <td className="td-right muted">
                    Cash:{" "}
                    {asCurrency(
                      transactions.reduce(
                        (s, t) => s + (t.TotalCashAmount || 0),
                        0
                      )
                    )}{" "}
                    · Card:{" "}
                    {asCurrency(
                      transactions.reduce(
                        (s, t) => s + (t.TotalCardAmount || 0),
                        0
                      )
                    )}
                  </td>
                  <td className="td-right">
                    {asCurrency(
                      transactions.reduce((s, t) => s + (t.TotalAmount || 0), 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
